import { abbrNumberFilter } from "app/filters/ngFilters";
import TrafficSignalTemplatesManager from "app/pages/sales-intelligence/sub-modules/signals/services/templates/traffic/TrafficSignalTemplatesManager";
import type TrafficChangesModel from "../../../models/traffic/TrafficChangesModel";
import type TrafficSignalFilters from "../../../models/traffic/TrafficSignalFilters";
import { isTrafficOverallChangeMetric } from "../../../models/traffic/type-guards/isTrafficOverallChangeMetric";
import { isTrafficSourceTypeMetric } from "../../../models/traffic/type-guards/isTrafficSourceTypeMetric";
import type { TrafficTemplateAlias } from "../../templates/traffic/types";
import { isTrafficEngagementMetric } from "app/pages/sales-intelligence/sub-modules/signals/models/traffic/type-guards/isTrafficEngagementMetric";

const compareByPredicate =
    <V>(predicate: readonly V[]) =>
    (a: V, b: V): number => {
        const aIndex = predicate.indexOf(a);
        const bIndex = predicate.indexOf(b);

        return aIndex - bIndex;
    };

const getTrafficMetricTypeIdentifier = (metric: string): ((metric: string) => boolean) | null => {
    switch (true) {
        case isTrafficOverallChangeMetric(metric):
            return isTrafficOverallChangeMetric;

        case isTrafficSourceTypeMetric(metric):
            return isTrafficSourceTypeMetric;

        case isTrafficEngagementMetric(metric):
            return isTrafficEngagementMetric;

        default:
            return null;
    }
};

const trafficSignalFiltersService = {
    getChangeValue<
        F extends {
            readonly value: number;
        },
    >(filters: F): number {
        const { value } = filters;

        return value * 100;
    },

    getThresholdValue<
        F extends {
            readonly thresholdValue: number;
        },
    >(filters: F): string {
        const { thresholdValue } = filters;

        return abbrNumberFilter()(thresholdValue);
    },

    getTrafficChannelsFilter(
        filters: Partial<TrafficSignalFilters>,
        trafficChangesParams: Partial<TrafficChangesModel>,
    ): Partial<TrafficSignalFilters> {
        const mergedTrafficChanges = filters.trafficChanges
            ? {
                  ...filters.trafficChanges,
                  ...trafficChangesParams,
              }
            : trafficChangesParams;
        return {
            ...filters,
            trafficChanges: mergedTrafficChanges,
        };
    },

    getFiltersOptionsBy<
        O extends {
            metrics?: readonly string[];
        },
    >(
        { metrics = [], ...rest }: Readonly<O>,
        alias: TrafficTemplateAlias,
    ): {
        metrics: readonly string[];
    } & Omit<O, "metrics"> {
        const predicate = TrafficSignalTemplatesManager.optionsByAlias[alias] ?? [];

        const filteredMetrics: readonly string[] = metrics
            .filter((metric) => predicate.includes(metric))
            .sort(compareByPredicate(predicate));

        return { metrics: filteredMetrics, ...rest };
    },

    checkIsTrafficMetricLastOfType(optionsArr: readonly string[]) {
        const numberOfOptions = optionsArr.length;

        return (element: string, index: number): boolean => {
            const matchMetricType = getTrafficMetricTypeIdentifier(element);

            if (!matchMetricType) {return false;}

            for (let i = index + 1; i < numberOfOptions; i++) {
                // If we find another element of the same type after the current one, return false
                if (matchMetricType(optionsArr[i])) {
                    return false;
                }
            }

            return index !== numberOfOptions - 1;
        };
    },
};

export default trafficSignalFiltersService;
